import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, BadgeCheck, Settings, LogOut, Camera, Heart, Sun, Moon,
  Pencil, Trash2, Tag, CheckCircle2, MessageCircle, Loader2, X, Package,
  UserPlus, UserCheck,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { uploadImage, IMAGE_ACCEPT } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useCurrency, CCY_SYMBOLS } from "@/context/CurrencyContext";
import ListingCard from "@/components/ListingCard";
import ConfirmDialog from "@/components/ConfirmDialog";
import { ListingCardSkeleton } from "@/components/Skeletons";
import { TIMEZONES } from "@/lib/utils";

const ROLES = ["Buyer", "Seller", "Broker"];

export default function Profile() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { userId } = useParams();
  const { user, logout } = useAuth();
  const isOwn = !userId || userId === user.id;
  const targetId = isOwn ? user.id : userId;
  const [showSettings, setShowSettings] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["profile", targetId],
    queryFn: async () => (await api.get(`/users/${targetId}/profile`)).data,
    refetchInterval: false,
  });

  const p = data?.user;
  const stats = data?.stats || { active: 0, sold: 0, total: 0, followers: 0, following: 0, is_following: false };
  const listings = data?.listings || [];
  const [delTarget, setDelTarget] = useState(null);
  const [followSheet, setFollowSheet] = useState(null);
  const [follow, setFollow] = useState({ following: false, followers: 0, followingCount: 0 });

  useEffect(() => {
    if (data?.stats) setFollow({ following: data.stats.is_following, followers: data.stats.followers, followingCount: data.stats.following });
  }, [data]);

  const toggleFollow = async () => {
    try {
      const { data: r } = await api.post(`/users/${targetId}/follow`);
      setFollow((f) => ({ ...f, following: r.following, followers: f.followers + (r.following ? 1 : -1) }));
      try { navigator.vibrate?.(10); } catch {}
    } catch { toast.error("Please log in"); }
  };

  const startChat = async () => {
    try {
      const { data: r } = await api.post("/conversations", { other_user_id: targetId });
      nav(`/messages/${r.conversation_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start chat"); }
  };

  const markSold = async (l) => {
    const next = l.status === "sold" ? "available" : "sold";
    try {
      await api.post(`/listings/${l.id}/status?status=${next}`);
      qc.invalidateQueries({ queryKey: ["profile", targetId] });
      toast.success(next === "sold" ? "Marked as sold" : "Marked available");
    } catch { toast.error("Failed"); }
  };

  const doDelete = async () => {
    await api.delete(`/listings/${delTarget.id}`);
    qc.invalidateQueries({ queryKey: ["profile", targetId] });
    qc.invalidateQueries({ queryKey: ["home-listings"] });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      {/* Premium header banner */}
      <div className="relative">
        <div className="h-36" style={{ background: "linear-gradient(135deg,#F59E0B,#D97706 60%,#FBBF24)" }} />
        <div className="absolute right-4 top-4 flex gap-2">
          {isOwn && (
            <button onClick={() => setShowSettings(true)} data-testid="profile-settings" className="glass pressable flex h-10 w-10 items-center justify-center rounded-full">
              <Settings size={18} color="#fff" />
            </button>
          )}
        </div>

        <div className="px-4">
          <div className="glass -mt-16 rounded-3xl p-5" style={{ boxShadow: "var(--shadow)" }}>
            <div className="flex items-end gap-4">
              <div className="relative -mt-14 flex h-24 w-24 items-center justify-center overflow-hidden rounded-3xl border-4 bg-accent text-3xl font-extrabold text-white"
                   style={{ borderColor: "var(--surface)" }}>
                {p?.avatar ? <img src={fileUrl(p.avatar)} alt="" className="h-full w-full object-cover" /> : (p?.name?.[0] || "?").toUpperCase()}
              </div>
              {!isOwn && (
                <div className="ml-auto flex gap-2">
                  <button onClick={toggleFollow} data-testid="profile-follow"
                          className="pressable flex items-center gap-2 rounded-full px-4 py-2.5 font-bold"
                          style={follow.following
                            ? { background: "var(--surface-2)", color: "var(--text)", border: "1px solid var(--border)" }
                            : { background: "linear-gradient(135deg,#F59E0B,#D97706)", color: "#fff" }}>
                    {follow.following ? <><UserCheck size={16} /> Following</> : <><UserPlus size={16} /> Follow</>}
                  </button>
                  <button onClick={startChat} data-testid="profile-message" className="glass pressable flex items-center gap-2 rounded-full px-4 py-2.5 font-bold">
                    <MessageCircle size={16} className="accent" />
                  </button>
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold">{p?.name || "…"}</h1>
              {p?.verified && <BadgeCheck size={20} className="accent" />}
            </div>
            {p?.location && <p className="mt-0.5 flex items-center gap-1 text-sm text-dim"><MapPin size={14} />{p.location}</p>}

            <div className="mt-3 flex flex-wrap gap-2">
              {(p?.roles || []).map((r) => (
                <span key={r} className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: "rgba(245,158,11,0.14)", color: "var(--accent)" }} data-testid={`role-badge-${r}`}>{r}</span>
              ))}
            </div>
            {p?.bio && <p className="mt-3 text-sm leading-relaxed text-dim">{p.bio}</p>}

            <div className="mt-3 flex items-center gap-4 text-sm">
              <button onClick={() => setFollowSheet("followers")} data-testid="followers-count" className="pressable"><b style={{ color: "var(--text)" }}>{follow.followers}</b> <span className="text-dim">followers</span></button>
              <button onClick={() => setFollowSheet("following")} data-testid="following-count" className="pressable"><b style={{ color: "var(--text)" }}>{follow.followingCount}</b> <span className="text-dim">following</span></button>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <StatBox n={stats.active} label="Active" />
              <StatBox n={stats.sold} label="Sold" />
              <StatBox n={stats.total} label="Total" />
            </div>

            {isOwn && (
              <div className="mt-4 flex gap-2">
                <button onClick={() => nav("/favorites")} data-testid="profile-favorites" className="glass pressable flex flex-1 items-center justify-center gap-2 rounded-2xl py-2.5 font-bold">
                  <Heart size={16} className="accent" /> Saved
                </button>
                <button onClick={() => setShowSettings(true)} data-testid="profile-edit" className="glass pressable flex flex-1 items-center justify-center gap-2 rounded-2xl py-2.5 font-bold">
                  <Pencil size={16} className="accent" /> Edit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Listings */}
      <div className="mb-3 mt-6 flex items-center justify-between px-4">
        <h2 className="font-display text-lg font-bold">{isOwn ? "My Listings" : "Listings"}</h2>
        <span className="text-sm text-dim">{listings.length}</span>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 px-4 md:grid-cols-3">{[0, 1].map((i) => <ListingCardSkeleton key={i} />)}</div>
      ) : listings.length === 0 ? (
        <div className="flex flex-col items-center px-4 py-16 text-center text-dim">
          <Package size={44} className="mb-3 opacity-40" />
          <p className="font-semibold">No listings yet</p>
          {isOwn && <button onClick={() => nav("/post")} className="btn-accent mt-4 px-6 py-2.5 font-bold">Post your first</button>}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 px-4 md:grid-cols-3">
          {listings.map((l, i) => (
            <div key={l.id} className="relative">
              <ListingCard listing={l} index={i} onOpen={() => nav(`/listing/${l.id}`)} />
              {isOwn && (
                <div className="mt-1.5 flex gap-1.5">
                  <ActionBtn onClick={() => markSold(l)} testid={`sold-${l.id}`} active={l.status === "sold"}><CheckCircle2 size={14} />{l.status === "sold" ? "Sold" : "Mark sold"}</ActionBtn>
                  <ActionBtn onClick={() => nav(`/post/${l.id}`)} testid={`edit-${l.id}`}><Tag size={14} />Edit</ActionBtn>
                  <ActionBtn onClick={() => setDelTarget(l)} testid={`del-${l.id}`} danger><Trash2 size={14} /></ActionBtn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {isOwn && (
        <div className="px-4 pt-6">
          <button onClick={async () => { await logout(); nav("/auth"); }} data-testid="logout-btn"
                  className="glass pressable flex w-full items-center justify-center gap-2 rounded-2xl py-3 font-bold" style={{ color: "var(--red)" }}>
            <LogOut size={18} /> Log out
          </button>
        </div>
      )}

      <AnimatePresence>{showSettings && <SettingsSheet onClose={() => setShowSettings(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["profile", targetId] })} />}</AnimatePresence>
      <ConfirmDialog open={!!delTarget} title="Delete listing?"
                     description="This permanently removes the listing and its comments. This cannot be undone."
                     confirmLabel="Delete" onConfirm={doDelete} onClose={() => setDelTarget(null)} />
      <AnimatePresence>
        {followSheet && <FollowListSheet key={followSheet} userId={targetId} kind={followSheet} onClose={() => setFollowSheet(null)} onOpenUser={(id) => { setFollowSheet(null); nav(`/u/${id}`); }} />}
      </AnimatePresence>
    </motion.div>
  );
}

function FollowListSheet({ userId, kind, onClose, onOpenUser }) {
  const { data } = useQuery({
    queryKey: ["follow-list", userId, kind],
    queryFn: async () => (await api.get(`/users/${userId}/${kind}`)).data.items,
  });
  const items = data || [];
  return (
    <>
      <motion.div className="fixed inset-0 z-[60]" style={{ background: "rgba(0,0,0,0.5)" }}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div className="fixed bottom-0 left-1/2 z-[70] max-h-[75vh] w-full -translate-x-1/2 overflow-y-auto goe-surface no-scrollbar"
                  style={{ maxWidth: 520, borderTopLeftRadius: 28, borderTopRightRadius: 28, borderTop: "1px solid var(--border)" }}
                  initial={{ y: "100%", x: "-50%" }} animate={{ y: 0, x: "-50%" }} exit={{ y: "100%", x: "-50%" }}
                  transition={{ type: "spring", stiffness: 320, damping: 32 }} data-testid={`follow-list-${kind}`}>
        <div className="mx-auto mt-3 h-1.5 w-12 rounded-full" style={{ background: "var(--border)" }} />
        <h3 className="font-display px-5 py-3 text-lg font-extrabold capitalize">{kind}</h3>
        <div className="space-y-1 px-3 pb-6">
          {items.length === 0 ? (
            <p className="py-10 text-center text-sm text-dim">No {kind} yet</p>
          ) : items.map((u) => (
            <button key={u.id} onClick={() => onOpenUser(u.id)} data-testid={`follow-user-${u.id}`}
                    className="pressable flex w-full items-center gap-3 rounded-2xl p-2.5 text-left" style={{ background: "var(--surface-2)" }}>
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-accent font-bold text-white" style={{ minWidth: 40 }}>
                {u.avatar ? <img src={fileUrl(u.avatar)} alt="" className="h-full w-full object-cover" /> : (u.name?.[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-bold" style={{ color: "var(--text)" }}>{u.name}</p>
                <p className="text-xs text-dim">{(u.roles || []).join(" · ")}</p>
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </>
  );
}

function StatBox({ n, label }) {
  return (
    <div className="rounded-2xl py-3 text-center" style={{ background: "var(--surface-2)" }}>
      <p className="font-display text-xl font-extrabold accent">{n}</p>
      <p className="text-xs text-dim">{label}</p>
    </div>
  );
}

function ActionBtn({ children, onClick, testid, danger, active }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="glass pressable flex flex-1 items-center justify-center gap-1 rounded-xl py-1.5 text-xs font-bold"
            style={{ color: danger ? "var(--red)" : active ? "var(--green)" : "var(--text)" }}>{children}</button>
  );
}

function SettingsSheet({ onClose, onSaved }) {
  const { user, updateUser } = useAuth();
  const { theme, set } = useTheme();
  const { currency, setCurrency, supported } = useCurrency();
  const fileRef = useRef();
  const [f, setF] = useState({ name: user.name, location: user.location || "", bio: user.bio || "" });
  const [roles, setRoles] = useState(user.roles || []);
  const [avatar, setAvatar] = useState(user.avatar);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [tz, setTz] = useState(user.timezone || "UTC");

  const changePw = async () => {
    if (pw.next.length < 6) return toast.error("New password must be at least 6 characters");
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change password"); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Images only");
    setUploading(true);
    try { const { url } = await uploadImage(file); setAvatar(url); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/users/me", { ...f, roles, avatar, currency, timezone: tz });
      updateUser(data.user);
      onSaved?.();
      toast.success("Profile updated");
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const toggleRole = (r) => setRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));

  return (
    <>
      <motion.div className="fixed inset-0 z-[60]" style={{ background: "rgba(0,0,0,0.5)" }}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div className="fixed bottom-0 left-1/2 z-[70] max-h-[90vh] w-full -translate-x-1/2 overflow-y-auto goe-surface no-scrollbar"
                  style={{ maxWidth: 520, borderTopLeftRadius: 28, borderTopRightRadius: 28, borderTop: "1px solid var(--border)" }}
                  initial={{ y: "100%", x: "-50%" }} animate={{ y: 0, x: "-50%" }} exit={{ y: "100%", x: "-50%" }} transition={{ type: "spring", stiffness: 320, damping: 32 }}>
        <div className="mx-auto mt-3 h-1.5 w-12 rounded-full" style={{ background: "var(--border)" }} />
        <div className="flex items-center justify-between px-5 pt-4">
          <h3 className="font-display text-xl font-extrabold">Settings</h3>
          <button onClick={onClose} className="pressable glass flex h-9 w-9 items-center justify-center rounded-full"><X size={16} /></button>
        </div>

        <div className="space-y-5 p-5">
          {/* Appearance — only Light / Dark */}
          <div>
            <Lbl>Appearance</Lbl>
            <div className="glass flex gap-2 rounded-2xl p-1.5">
              <ThemeBtn active={theme === "light"} onClick={() => set("light")} icon={Sun} label="Light" testid="theme-light" />
              <ThemeBtn active={theme === "dark"} onClick={() => set("dark")} icon={Moon} label="Dark" testid="theme-dark" />
            </div>
          </div>

          {/* Avatar */}
          <div className="flex items-center gap-4">
            <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload" className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl bg-accent text-2xl font-extrabold text-white">
              {avatar ? <img src={fileUrl(avatar)} alt="" className="h-full w-full object-cover" /> : (f.name?.[0] || "?").toUpperCase()}
              <span className="absolute bottom-0 left-0 right-0 flex items-center justify-center bg-black/50 py-1">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
              </span>
            </button>
            <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} hidden onChange={onAvatar} />
            <p className="text-sm text-dim">Tap to change<br />profile picture</p>
          </div>

          <Field label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="settings-name" />
          <Field label="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} testid="settings-location" />
          <div>
            <Lbl>Bio</Lbl>
            <textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} rows={3} maxLength={500} data-testid="settings-bio"
                      className="glass w-full rounded-2xl px-4 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
          </div>

          <div>
            <Lbl>Roles</Lbl>
            <div className="flex gap-2">
              {ROLES.map((r) => (
                <button key={r} onClick={() => toggleRole(r)} data-active={roles.includes(r)} data-testid={`settings-role-${r}`}
                        className="chip pressable flex-1 py-2.5 text-sm font-bold">{r}</button>
              ))}
            </div>
          </div>

          <div>
            <Lbl>Default Currency</Lbl>
            <div className="no-scrollbar flex gap-2 overflow-x-auto">
              {supported.map((c) => (
                <button key={c} onClick={() => setCurrency(c)} data-active={currency === c} data-testid={`settings-ccy-${c}`}
                        className="chip pressable shrink-0 px-4 py-2 text-sm font-bold"><span className="mr-1">{CCY_SYMBOLS[c]}</span>{c}</button>
              ))}
            </div>
          </div>

          <div>
            <Lbl>Time Zone</Lbl>
            <div className="flex gap-2">
              {TIMEZONES.map((t) => (
                <button key={t.id} onClick={() => setTz(t.id)} data-active={tz === t.id} data-testid={`settings-tz-${t.label}`}
                        className="chip pressable flex-1 py-2.5 text-sm font-bold">{t.label}</button>
              ))}
            </div>
            <p className="mt-1.5 px-1 text-[11px] text-dim">Used for price timestamps and your 08:00 daily summary</p>
          </div>

          <div>
            <Lbl>Change Password</Lbl>
            <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder="Current password" data-testid="pw-current"
                   className="glass mb-2 w-full rounded-2xl px-4 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
            <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="New password" data-testid="pw-new"
                   className="glass mb-2 w-full rounded-2xl px-4 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
            <button onClick={changePw} data-testid="pw-change" className="glass pressable w-full rounded-2xl py-2.5 font-bold" style={{ color: "var(--accent)" }}>Update Password</button>
          </div>

          <button onClick={save} disabled={saving} data-testid="settings-save" className="btn-accent flex w-full items-center justify-center gap-2 py-3.5 font-extrabold">
            {saving ? <Loader2 className="animate-spin" size={20} /> : "Save Changes"}
          </button>
        </div>
      </motion.div>
    </>
  );
}

const Lbl = ({ children }) => <p className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-dim">{children}</p>;
function Field({ label, testid, ...props }) {
  return (<div><Lbl>{label}</Lbl><input {...props} data-testid={testid} className="glass w-full rounded-2xl px-4 py-3.5 text-[15px] outline-none" style={{ color: "var(--text)" }} /></div>);
}
function ThemeBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="pressable relative flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold" style={{ color: active ? "#fff" : "var(--text-dim)" }}>
      {active && <motion.div layoutId="themebtn" className="absolute inset-0 rounded-xl" style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }} />}
      <span className="relative flex items-center gap-2"><Icon size={16} />{label}</span>
    </button>
  );
}
